import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DEF_PG_SIZE_FILTER } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { PaginationContext } from '../../contexts/table/PaginationContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Filter } from '../../models/Filter';
import { Lookup } from '../../models/Lookup';
import { showError } from '../../services/utilities/ServiceError';
import { appendFilter } from '../../utilities/filters';
import { isClearLocked, isStaticDefaultState, resolveFilterClearPolicy } from '../../utilities/filterClearPolicy';
import { matchesOptionValue } from '../../utilities/genericUtil';
import { buildSearchTerm } from '../../utilities/SearchDescriptors';
import Chip from '../chip/chip';
import OutsideClick from '../outside-click/outside-click';
import PaginatedSearchableList from '../selection-field/paginated-searchable-list';
import Tooltip from '../Tooltip/Tooltip';
import closeIcon from './../../images/blue_close.svg';

export interface PaginatedFilterChipProps {
    accessor: string;
    filters: Filter[];
    lookupList: Lookup[];
    handleItemClick: any;
    removeFilterWithKey: any;
    toggleFilterModal: (trigger: string) => void;
    disableFilterToggling: boolean
}

export const pageSize = DEF_PG_SIZE_FILTER
export const initialPagination = {
    totalCount: pageSize,
    maxPageSize: pageSize,
    totalPages: 1,
    pageNumber: 1
};

export const PaginatedFilterChip: FC<PaginatedFilterChipProps> = ({ accessor, filters, lookupList, handleItemClick, removeFilterWithKey, toggleFilterModal, disableFilterToggling = false }) => {
    const [filterPagination, setFilterPagination] = useState(initialPagination);
    const [isOpen, setIsOpen] = useState(false);
    const [listItems, setListItems] = useState(new Array(0));
    const [selectedItems, setSelectedItems] = useState(new Array(0));
    const [searchTerm, setSearchTerm] = useState('');
    const isLoadingRef = useRef(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // Tracks whether this component instance is still mounted. Set to false on unmount so that
    // in-flight API calls in getData do not attempt to call setState after the component is gone.
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    const filter = useMemo(() => filters.find((filter) => filter.key === accessor), [filters, accessor]);
    const lookup = useMemo(() => lookupList.find(item => accessor.startsWith(item.name)), [lookupList, accessor]);
    const { translate } = useLocale();

    const getData = useCallback(async () => {
        if (!lookup || isLoadingRef.current) {
            return;
        }
        isLoadingRef.current = true;
        try {             
            const newSearchTerm = lookup.name && searchTerm ? buildSearchTerm(lookup.searchAccessor, searchTerm) : undefined;
            let filtersToApply: Filter[] = [];

            if (lookup.filterDependentKey) {
                const dependentFilter = filters.find(f => f.key === lookup.filterDependentKey);
                const selectedValues = dependentFilter?.selected.map(item => item.value).join(",");
                
                if (selectedValues) {
                    filtersToApply = appendFilter(selectedValues, lookup.filterDependentKey, lookup.filterDependentKey, []);
                }
            }

            const { rows, pagination } = lookup.dataMethod
                ? await lookup.dataMethod({
                    filters: filtersToApply,
                    goToPage: filterPagination.pageNumber,
                    pageSize: filterPagination.maxPageSize,
                    searchTerm: newSearchTerm
                })
                : (() => {
                    const staticValues = lookup.values ?? [];
                    const filteredValues = searchTerm
                        ? staticValues.filter((option: any) => {
                            const label = `${option?.label ?? ""}`.toLowerCase();
                            const value = `${option?.value ?? ""}`.toLowerCase();
                            const term = searchTerm.toLowerCase();
                            return label.includes(term) || value.includes(term);
                        })
                        : staticValues;

                    const totalCount = filteredValues.length;
                    const totalPages = Math.max(1, Math.ceil((totalCount || 1) / filterPagination.maxPageSize));
                    const currentPage = Math.min(filterPagination.pageNumber, totalPages);
                    const startIndex = (currentPage - 1) * filterPagination.maxPageSize;
                    const pagedValues = filteredValues.slice(startIndex, startIndex + filterPagination.maxPageSize);

                    return {
                        rows: pagedValues,
                        pagination: {
                            ...filterPagination,
                            pageNumber: currentPage,
                            totalCount,
                            totalPages,
                        }
                    };
                })();

            if (isMountedRef.current) {
                setListItems(rows);
                setFilterPagination(pagination);
            }
        } catch (error) {
            if (isMountedRef.current) {
                showError(error, toast.warn);
            }
        } finally {
            isLoadingRef.current = false;
        }
    }, [lookup, searchTerm, filterPagination.pageNumber, filterPagination.maxPageSize, filters, toast]);

    useEffect(() => {
        if (!lookup || !isOpen) {
            return;
        }
        getData();
    }, [getData, lookup, isOpen]);

    useEffect(() => {        
        if (isOpen && listItems.length > 0) {
            const filter = filters.find((filter) => filter.key === accessor);
            if (filter) {
                const newSelectedItems = mapOptionsToSelectedIndexes(filter, listItems);
                setSelectedItems(newSelectedItems ?? []);
            }
        }
    }, [isOpen, filter, accessor, listItems]);

    const mapOptionsToSelectedIndexes = (filter, options) => {
        return options
            .map((option) => filter.selected?.some((item) => matchesOptionValue(item.value, option.value)) ? option.value : null)
            .filter((value) => value !== null);
    };

    const handleSearch = (searchTerm) => {        
        setSearchTerm(searchTerm);
        setFilterPagination(prev => ({ ...prev, pageNumber: 1 }));
    };

    const toggleOpen = () => {
        setIsOpen(prev => !prev);
    };

    const updatePageNumber = (pageNumber: number) => {
        setFilterPagination(prev => ({ ...prev, pageNumber }));
    }

    const isSingleSelect = (): boolean => {
        return lookupList.find(item => accessor.startsWith(item.name))?.isSingleSelect ?? false;
    }

    const isRequired = (): boolean => {
        return filter && filter.selected && filter.selected.length > 0 ? filter.selected[0].value === "require_filter" : false;
    }

    // Stable reference so useOutSideClick does not remove and re-add the document mousedown
    // listener on every render. Must be declared at the top level (not inside JSX) to comply
    // with the Rules of Hooks. Uses a ref to access current isOpen state without recreating
    // the callback, preventing stale closures after column selector interactions.
    const isOpenRef = useRef(isOpen);
    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);
    
    const handleOutsideClick = useCallback(() => { 
        if (isOpenRef.current) { 
            setIsOpen(false); 
        } 
    }, []);

    // After the dropdown becomes visible, check whether it overflows the right edge of the viewport.
    // If it does, shift it left by exactly the overflow amount (plus 8px breathing room) using a CSS
    // transform so it stays fully on screen regardless of where the chip is anchored.
    // A ResizeObserver is used (rather than a one-shot useEffect) because the list inside is
    // populated asynchronously — by the time data arrives the dropdown may be wider than when
    // it first opened, so we need to re-correct every time its size changes.
    useEffect(() => {
        if (!isOpen || !dropdownRef.current) {
            return;
        }

        const el = dropdownRef.current;
        const adjust = () => {
            el.style.transform = '';
            const rect = el.getBoundingClientRect();
            const overflow = rect.right - window.innerWidth;
            el.style.transform = overflow > 0 ? `translateX(-${overflow + 8}px)` : '';
        };
        const observer = new ResizeObserver(adjust);
        observer.observe(el);
        adjust();
        return () => observer.disconnect();
    }, [isOpen]);

    return <Chip className={`flex flex-row items-center py-x text-portal-blue-primary ml-2 filter-chip mb-2 relative  ${isRequired() ? `border-red-700 border-2` : ''}`}>
        <span className="font-bold px-2" style={{ textOverflow: "ellipsis" }}>{filter?.label}:</span>

        {accessor.includes("dateOnly") ?
            <button type="button" data-testid="button-PaginatedFilterChip-1" className={`flex flex-row items-center pr-2 ${disableFilterToggling ? "cursor-not-allowed" : ""}`} onClick={() => toggleFilterModal(accessor)}>
                {
                    filter &&
                        filter.selected &&
                        filter.selected.length > 0 ?
                        filter.selected.length > 1 ?
                            translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length]) : filter.selected[0].label.replaceAll("-", "/").replaceAll(";", " - ") : ''
                }
            </button>
            : accessor === "time" ?
                <button type="button" data-testid="button-PaginatedFilterChip-1" className={`flex flex-row items-center pr-2`} onClick={() => toggleFilterModal(accessor)}>
                    {filter && filter.selected && filter.selected.length > 0 ? filter.selected[0].label : ''}
                </button>
            : accessor === "inactiveFor" ?                                        
                <button type="button" data-testid="button-PaginatedFilterChip-1" className={`flex flex-row items-center pr-2 ${disableFilterToggling ? "cursor-not-allowed" : ""}`} onClick={() => toggleFilterModal(accessor)}>
                    {filter && filter.selected && filter.selected.length > 0 ? filter.selected[0].label : ''}
                </button>
                : <>

                    <button type="button" className="flex flex-row items-center pr-1" onClick={toggleOpen}>
                        {filter && filter.selected && filter.selected.length > 0 ? (
                            filter.selected.length > 1 ? (
                                <Tooltip show text={translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length])} direction="bottom">
                                    <span>{translate.byKeyFormatted(TranslationKeys.selectedWithCount, [filter.selected.length])}</span>
                                </Tooltip>
                            ) : (
                                <Tooltip show text={filter.selected[0].label} direction="bottom">
                                    <p
                                        style={{ textOverflow: 'ellipsis', fontSize: '11px', textAlign: 'end' }}
                                        className={`overflow-hidden whitespace-nowrap relative filter-chip-text ${isRequired() ? 'text-red-700' : ''}`}>
                                        {filter.selected[0].label}
                                    </p>
                                </Tooltip>
                            )
                        ) : (
                            ''
                        )}
                    </button>

                    <>
                        <button type="button" data-testid="button-PaginatedFilterChip-2" className="flex flex-row items-center px-1" onClick={toggleOpen}>
                            <FontAwesomeIcon className="pl-1 text-portal-blue-primary" size='lg' icon={faChevronDown} />
                        </button>
                        <PaginationContext.Provider value={{ isInUse: true, pagination: filterPagination, updatePageNumber, updatePageSize: () => { } }}>
                            {/* Stable callback prevents useOutSideClick from removing and re-adding the
                                document mousedown listener on every render, which could cause the
                                listener to be briefly absent during rapid re-render cycles. */}
                            <OutsideClick onClick={handleOutsideClick} >
                            
                                <div ref={dropdownRef} className={`w-max left-0 absolute bg-white text-black shadow-md -mr-3 rounded-md px-2 py-2 z-50 ${isOpen ? '' : 'hidden'}`} style={{ top: '2rem' }}>
                                    <PaginatedSearchableList
                                        items={listItems}
                                        onSelect={(index, item) => { handleItemClick(item, index, accessor, listItems) }}
                                        isStriped={true}
                                        isMultiSelect={isSingleSelect() ? false : true}
                                        selectedItems={selectedItems}
                                        onSearch={handleSearch}
                                        searchable={lookupList.find(item => accessor.startsWith(item.name))?.searchAccessor ? true : false}>
                                    </PaginatedSearchableList>
                                </div>
                            </OutsideClick>
                        </PaginationContext.Provider>
                    </>
                </>
        }
        {
            disableFilterToggling
            || isClearLocked(filter)
            || (resolveFilterClearPolicy(filter) === "restore-default" && isStaticDefaultState(filter)) ? null :
                <button className="mx-1 mr-2 cursor-pointer w-5" type='button' onClick={() => {
                    removeFilterWithKey(accessor);
                }}>
                    <img src={closeIcon} alt="" />
                </button>
        }
    </Chip>
};
